<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  patchMarketplaceJson,
  postMarketplaceJsonBody,
  postMarketplaceForm,
} from 'dashboard/helpers/marketplaceApi';
import BankGroupCard from './BankGroupCard.vue';

const route = useRoute();
const accountId = route.params.accountId;

const COMPANIES = [
  { v: 'koza', l: 'Koza E-Ticaret' },
  { v: 'globaltech', l: 'Globaltech (ModaXLarge)' },
  { v: 'emrah', l: 'Emrah Karabayır (Şahsi)' },
];
const SIDE_ORDER = ['income', 'expense', 'transfer'];

const company = ref('koza');
const startDate = ref('');
const endDate = ref('');
const overview = ref(null);
const loading = ref(false);

// Ekstre yükleme
const fileInput = ref(null);
const uploading = ref(false);
const uploadMsg = ref('');
const companyLabel = computed(
  () => COMPANIES.find(c => c.v === company.value)?.l || ''
);

const expanded = ref({}); // key -> bool
const groupTxns = ref({}); // key -> txn[]
const groupLoading = ref({}); // key -> bool

// Sürükle-bırak — hem hareket (txn) hem ana grup taşınabilir
const dragKind = ref(null); // 'txn' | 'group' | null
const draggingTxn = ref(null);
const draggingGroup = ref(null);
const dragOverKey = ref(''); // txn bırakma hedefi (kart vurgusu)
const dropTarget = ref(null); // grup: { key, edge:'top'|'bottom' } veya { endSide }

// Kâr-Zarara Esas Gider havuzu
const oneOffs = ref([]); // tek tek işaretlenmiş hareketler
const pnlZoneActive = ref(false); // havuz üstüne sürükleme vurgusu

const base = () => `bank_ledger/${company.value}`;
const dateParams = () => {
  const p = new URLSearchParams();
  if (startDate.value) p.set('start', startDate.value);
  if (endDate.value) p.set('end', endDate.value);
  return p;
};

const tl = v =>
  (v == null ? 0 : v).toLocaleString('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    maximumFractionDigits: 0,
  });

const allGroups = computed(() => overview.value?.groups || []);
const incomeGroups = computed(() =>
  allGroups.value.filter(g => g.side === 'income')
);
const expenseGroups = computed(() =>
  allGroups.value.filter(g => g.side === 'expense')
);
const transferGroups = computed(() =>
  allGroups.value.filter(g => g.side === 'transfer')
);
const pnlGroups = computed(() => allGroups.value.filter(g => g.pnl));
const pnlTotalAbs = computed(() => Math.abs(overview.value?.pnl_total || 0));
// Bir şey sürüklenirken havuz "hazır" görünsün (kullanıcı nereye bırakacağını görsün).
const pnlArmed = computed(() => dragKind.value !== null);

const loadOverview = async () => {
  const p = dateParams();
  overview.value = await fetchMarketplaceJson(
    accountId,
    `${base()}/overview?${p.toString()}`
  );
};

const loadGroupTxns = async key => {
  groupLoading.value = { ...groupLoading.value, [key]: true };
  try {
    const p = dateParams();
    p.set('group', key);
    p.set('per_page', '3000');
    const res = await fetchMarketplaceJson(
      accountId,
      `${base()}/transactions?${p.toString()}`
    );
    groupTxns.value = { ...groupTxns.value, [key]: res.transactions };
  } finally {
    groupLoading.value = { ...groupLoading.value, [key]: false };
  }
};

const toggle = async key => {
  const next = !expanded.value[key];
  expanded.value = { ...expanded.value, [key]: next };
  if (next && !groupTxns.value[key]) await loadGroupTxns(key);
};

const loadOneOffs = async () => {
  const res = await fetchMarketplaceJson(
    accountId,
    `${base()}/pnl/one_offs?${dateParams().toString()}`
  );
  oneOffs.value = res.transactions;
};

const reload = async () => {
  loading.value = true;
  try {
    await loadOverview();
    await loadOneOffs();
    const openKeys = Object.keys(expanded.value).filter(k => expanded.value[k]);
    groupTxns.value = {};
    await Promise.all(openKeys.map(k => loadGroupTxns(k)));
  } finally {
    loading.value = false;
  }
};

// ── Ekstre yükle: seçili şirketin .xlsx'ini gönder → parser + import → yenile ──
const onUpload = async e => {
  const file = e.target.files?.[0];
  if (!file) return;
  uploading.value = true;
  uploadMsg.value = '';
  try {
    const fd = new FormData();
    fd.append('file', file);
    const res = await postMarketplaceForm(accountId, `${base()}/upload`, fd);
    uploadMsg.value = `✓ ${res.created} yeni hareket eklendi, ${res.updated} mevcut hareket güncellendi.`;
    await reload();
  } catch (err) {
    uploadMsg.value = `Hata: ${err.message}`;
  } finally {
    uploading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};

// ── Sürükleme durumu ─────────────────────────────────────────────────────────
const onTxnDragStart = t => {
  dragKind.value = 'txn';
  draggingTxn.value = t;
};
const onGroupDragStart = g => {
  dragKind.value = 'group';
  draggingGroup.value = g;
};
const clearDrag = () => {
  dragKind.value = null;
  draggingTxn.value = null;
  draggingGroup.value = null;
  dragOverKey.value = '';
  dropTarget.value = null;
};

// ── Kâr-Zarara Esas Gider havuzu ─────────────────────────────────────────────
// Bir gider grubunu Kâr-Zarara dahil et / çıkar (kart 🎯 butonu, havuza sürükle, çip ✕).
const setGroupPnl = async (key, pnl) => {
  await postMarketplaceJsonBody(accountId, `${base()}/groups/pnl`, {
    group: key,
    pnl,
  });
  await reload();
};
const togglePnl = g => setGroupPnl(g.key, !g.pnl);
const removePnlGroup = key => setGroupPnl(key, false);

// Havuza bırak: gider grubunu (grup işareti) veya tek hareketi (override) dahil et.
const onPnlDrop = async () => {
  const kind = dragKind.value;
  const dg = draggingGroup.value;
  const dt = draggingTxn.value;
  clearDrag();
  pnlZoneActive.value = false;
  if (kind === 'group' && dg) {
    await setGroupPnl(dg.key, true);
  } else if (kind === 'txn' && dt) {
    await patchMarketplaceJson(
      accountId,
      `${base()}/transactions/${dt.id}/pnl`,
      { pnl: true }
    );
    await reload();
  }
};

const removePnlTxn = async id => {
  await patchMarketplaceJson(accountId, `${base()}/transactions/${id}/pnl`, {
    pnl: null,
  });
  await reload();
};

// Kartlar HEM hareket (txn) HEM grup bırakma hedefidir.
const txnCanDrop = g => {
  const d = draggingTxn.value;
  if (!d || g.key === d.group) return false;
  if (g.side === 'transfer') return true;
  return d.amount >= 0 ? g.side === 'income' : g.side === 'expense';
};
const accepts = g => dragKind.value === 'txn' && txnCanDrop(g);

// Grup sürüklenirken kartın üst/alt kenarındaki ekleme çizgisi.
const onGroupDragover = (g, edge) => {
  if (!draggingGroup.value || g.key === draggingGroup.value.key) return;
  dropTarget.value = { key: g.key, edge };
};
const insertEdgeFor = g =>
  dragKind.value === 'group' &&
  dropTarget.value &&
  dropTarget.value.key === g.key
    ? dropTarget.value.edge
    : '';

// ── Ana grup yerleşimi (taraf + sıra) ────────────────────────────────────────
const persistLayout = async ordered => {
  const groups = ordered.map((g, i) => ({
    key: g.key,
    side: g.side,
    position: i,
  }));
  await postMarketplaceJsonBody(accountId, `${base()}/groups/layout`, {
    groups,
  });
  await reload();
};

// Sıralı tam listeyi, bir tarafın kendi içindeki (yeniden düzenlenmiş) listesiyle kur.
const rebuildWithSide = (side, sideList, excludeKey) => {
  const result = [];
  SIDE_ORDER.forEach(s => {
    if (s === side) result.push(...sideList);
    else
      result.push(
        ...allGroups.value.filter(x => x.side === s && x.key !== excludeKey)
      );
  });
  return result;
};

// Karta bırak: sürüklenen grubu hedef kartın üst/alt kenarına göre yerleştir.
const onCardDrop = async g => {
  if (dragKind.value === 'txn') {
    const d = draggingTxn.value;
    clearDrag();
    if (!d) return;
    await patchMarketplaceJson(
      accountId,
      `${base()}/transactions/${d.id}/group`,
      { group: g.key }
    );
    await reload();
    return;
  }
  const dg = draggingGroup.value;
  const edge = dropTarget.value?.edge || 'top';
  clearDrag();
  if (!dg || dg.key === g.key) return;
  const side = g.side;
  const sideList = allGroups.value.filter(
    x => x.side === side && x.key !== dg.key
  );
  let idx = sideList.findIndex(x => x.key === g.key);
  if (idx < 0) idx = sideList.length;
  if (edge === 'bottom') idx += 1;
  sideList.splice(idx, 0, { ...dg, side });
  await persistLayout(rebuildWithSide(side, sideList, dg.key));
};

// Kolon sonu bırakma alanı (boş kolon + "sona ekle").
const onEndOver = (side, ev) => {
  if (dragKind.value !== 'group') return;
  ev.preventDefault();
  dropTarget.value = { endSide: side };
};
const endActive = side =>
  dragKind.value === 'group' && dropTarget.value?.endSide === side;
const onEndDrop = async side => {
  const dg = draggingGroup.value;
  clearDrag();
  if (!dg) return;
  const sideList = allGroups.value.filter(
    x => x.side === side && x.key !== dg.key
  );
  sideList.push({ ...dg, side });
  await persistLayout(rebuildWithSide(side, sideList, dg.key));
};

// Yukarı/aşağı buton: kendi tarafı içinde komşuyla yer değiştir.
const moveGroup = async (g, dir) => {
  const sideList = allGroups.value.filter(x => x.side === g.side);
  const i = sideList.findIndex(x => x.key === g.key);
  const j = i + dir;
  if (j < 0 || j >= sideList.length) return;
  [sideList[i], sideList[j]] = [sideList[j], sideList[i]];
  await persistLayout(rebuildWithSide(g.side, sideList, null));
};

// ⇄ buton: tarafı döngüle (gelir → gider → nötr → gelir), yeni tarafın sonuna.
const cycleSide = async g => {
  const next = SIDE_ORDER[(SIDE_ORDER.indexOf(g.side) + 1) % SIDE_ORDER.length];
  const rest = allGroups.value.filter(x => x.key !== g.key);
  await persistLayout([...rest, { ...g, side: next }]);
};

const isFirst = g => {
  const list = allGroups.value.filter(x => x.side === g.side);
  return list.length > 0 && list[0].key === g.key;
};
const isLast = g => {
  const list = allGroups.value.filter(x => x.side === g.side);
  return list.length > 0 && list[list.length - 1].key === g.key;
};

watch(company, () => {
  expanded.value = {};
  groupTxns.value = {};
  reload();
});
onMounted(() => reload());
</script>

<template>
  <div
    class="flex flex-col w-full h-full overflow-auto bg-slate-25 dark:bg-slate-900"
  >
    <div class="p-4 mx-auto w-full max-w-[1280px]">
      <!-- Başlık + şirket sekmeleri -->
      <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-50">
          Banka Defteri
        </h1>
        <div class="flex p-1 bg-slate-100 rounded-lg dark:bg-slate-800">
          <button
            v-for="c in COMPANIES"
            :key="c.v"
            class="px-4 py-1.5 text-sm font-medium rounded-md transition-colors"
            :class="
              company === c.v
                ? 'bg-white shadow text-slate-900 dark:bg-slate-700 dark:text-white'
                : 'text-slate-500 dark:text-slate-400'
            "
            @click="company = c.v"
          >
            {{ c.l }}
          </button>
        </div>
      </div>

      <!-- Tarih filtresi -->
      <div class="flex flex-wrap items-end gap-3 mb-4">
        <label class="flex flex-col text-xs text-slate-500">
          Başlangıç
          <input
            v-model="startDate"
            type="date"
            class="mt-1 px-2 py-1 text-sm border rounded border-slate-200 dark:bg-slate-800 dark:border-slate-700"
          />
        </label>
        <label class="flex flex-col text-xs text-slate-500">
          Bitiş
          <input
            v-model="endDate"
            type="date"
            class="mt-1 px-2 py-1 text-sm border rounded border-slate-200 dark:bg-slate-800 dark:border-slate-700"
          />
        </label>
        <button
          class="px-3 py-1.5 text-sm text-white rounded bg-woot-500 hover:bg-woot-600"
          @click="reload"
        >
          Uygula
        </button>
        <span v-if="loading" class="text-sm text-slate-400">Yükleniyor…</span>
      </div>

      <!-- Ekstre yükle: seçili şirketin VakıfBank .xlsx ekstresini yükle ve güncelle -->
      <div
        class="flex flex-wrap items-center gap-3 p-3 mb-4 border rounded-lg bg-woot-25 border-woot-100 dark:bg-woot-900/20 dark:border-woot-800"
      >
        <span class="text-sm text-slate-700 dark:text-slate-200">
          📥 <b>{{ companyLabel }}</b> ekstresini yükle
        </span>
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx"
          class="hidden"
          @change="onUpload"
        />
        <button
          class="px-3 py-1.5 text-sm text-white rounded bg-woot-500 hover:bg-woot-600 disabled:opacity-50"
          :disabled="uploading"
          @click="fileInput?.click()"
        >
          {{ uploading ? 'İşleniyor…' : 'Dosya seç ve güncelle' }}
        </button>
        <span
          v-if="uploadMsg"
          class="text-sm font-medium text-slate-700 dark:text-slate-200"
        >
          {{ uploadMsg }}
        </span>
        <span class="text-xs text-slate-400">
          VakıfBank .xlsx ekstresi — eski hareketler korunur, sadece yenisi
          eklenir; elle yaptığın gruplamalar bozulmaz.
        </span>
      </div>

      <!-- Özet: sadece gelen / giden / nötr (kâr-zarar hesabı değil) -->
      <div v-if="overview" class="grid grid-cols-1 gap-3 mb-4 sm:grid-cols-3">
        <div class="p-4 bg-white rounded-lg shadow-sm dark:bg-slate-800">
          <div class="text-xs text-slate-500">Toplam Gelir (gelen para)</div>
          <div class="text-xl font-semibold text-green-700 dark:text-green-400">
            {{ tl(overview.income) }}
          </div>
        </div>
        <div class="p-4 bg-white rounded-lg shadow-sm dark:bg-slate-800">
          <div class="text-xs text-slate-500">Toplam Gider (giden para)</div>
          <div class="text-xl font-semibold text-ruby-700 dark:text-ruby-400">
            {{ tl(overview.expense) }}
          </div>
        </div>
        <div class="p-4 bg-white rounded-lg shadow-sm dark:bg-slate-800">
          <div class="text-xs text-slate-500">
            Nötr (ayrıştırılan — hesaba katılmaz)
          </div>
          <div class="text-xl font-semibold text-slate-500 dark:text-slate-400">
            {{ tl(overview.transfer) }}
          </div>
        </div>
      </div>

      <!-- Kullanım ipucu -->
      <div
        class="flex items-center gap-2 p-3 mb-4 text-sm border rounded-lg bg-woot-25 border-woot-100 text-slate-600 dark:bg-woot-900/20 dark:border-woot-800 dark:text-slate-300"
      >
        <span>💡</span>
        <span>
          Hareketi <b>tutup sürükle</b>, doğru grubun üstüne bırak. Ana grubu
          <b>⠿ tutamağından</b> sürükleyip başka bir grubun üstüne (üst/alt
          kenardaki <b>mavi çizgi</b> nereye gireceğini gösterir) ya da kolon
          sonundaki <b>“sona bırak”</b> alanına bırak. <b>▲▼</b> ile sırala,
          <b>⇄</b> ile taraf değiştir. Gelir/gidere karışmasını istemediğin
          kalemi <b>Nötr</b> alana bırak.
        </span>
      </div>

      <!-- İki yan: Gelirler | Giderler -->
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <!-- GELİRLER -->
        <div>
          <div
            class="flex items-center justify-between px-1 mb-2 text-sm font-bold tracking-wide uppercase text-green-700 dark:text-green-400"
          >
            <span>▲ Gelirler</span>
            <span class="tabular-nums">{{ tl(overview?.income) }}</span>
          </div>
          <div class="flex flex-col gap-2">
            <BankGroupCard
              v-for="g in incomeGroups"
              :key="g.key"
              :group="g"
              :txns="groupTxns[g.key] || []"
              :loading="groupLoading[g.key]"
              :expanded="expanded[g.key]"
              :drag-kind="dragKind"
              :highlight="dragOverKey === g.key"
              :accepts="accepts(g)"
              :insert-edge="insertEdgeFor(g)"
              :muted="g.count === 0"
              :is-first="isFirst(g)"
              :is-last="isLast(g)"
              @toggle="toggle(g.key)"
              @move-up="moveGroup(g, -1)"
              @move-down="moveGroup(g, 1)"
              @cycle-side="cycleSide(g)"
              @group-dragstart="onGroupDragStart(g)"
              @group-dragend="clearDrag"
              @group-dragover="onGroupDragover(g, $event)"
              @card-dragover="dragOverKey = g.key"
              @card-dragleave="dragOverKey = ''"
              @card-drop="onCardDrop(g)"
              @txn-dragstart="onTxnDragStart"
              @txn-dragend="clearDrag"
            />
            <div
              v-if="dragKind === 'group'"
              class="flex items-center justify-center h-12 text-xs border-2 border-dashed rounded-lg transition-colors"
              :class="
                endActive('income')
                  ? 'border-woot-400 bg-woot-100/70 text-woot-700 dark:bg-woot-900/40'
                  : 'border-slate-200 text-slate-400 dark:border-slate-600'
              "
              @dragover="onEndOver('income', $event)"
              @drop="onEndDrop('income')"
            >
              Gelir tarafının sonuna bırak
            </div>
          </div>
        </div>

        <!-- GİDERLER -->
        <div>
          <div
            class="flex items-center justify-between px-1 mb-2 text-sm font-bold tracking-wide uppercase text-ruby-700 dark:text-ruby-400"
          >
            <span>▼ Giderler</span>
            <span class="tabular-nums">{{ tl(overview?.expense) }}</span>
          </div>
          <div class="flex flex-col gap-2">
            <BankGroupCard
              v-for="g in expenseGroups"
              :key="g.key"
              :group="g"
              :txns="groupTxns[g.key] || []"
              :loading="groupLoading[g.key]"
              :expanded="expanded[g.key]"
              :drag-kind="dragKind"
              :highlight="dragOverKey === g.key"
              :accepts="accepts(g)"
              :insert-edge="insertEdgeFor(g)"
              :muted="g.count === 0"
              :is-first="isFirst(g)"
              :is-last="isLast(g)"
              @toggle="toggle(g.key)"
              @move-up="moveGroup(g, -1)"
              @move-down="moveGroup(g, 1)"
              @cycle-side="cycleSide(g)"
              @toggle-pnl="togglePnl(g)"
              @group-dragstart="onGroupDragStart(g)"
              @group-dragend="clearDrag"
              @group-dragover="onGroupDragover(g, $event)"
              @card-dragover="dragOverKey = g.key"
              @card-dragleave="dragOverKey = ''"
              @card-drop="onCardDrop(g)"
              @txn-dragstart="onTxnDragStart"
              @txn-dragend="clearDrag"
            />
            <div
              v-if="dragKind === 'group'"
              class="flex items-center justify-center h-12 text-xs border-2 border-dashed rounded-lg transition-colors"
              :class="
                endActive('expense')
                  ? 'border-woot-400 bg-woot-100/70 text-woot-700 dark:bg-woot-900/40'
                  : 'border-slate-200 text-slate-400 dark:border-slate-600'
              "
              @dragover="onEndOver('expense', $event)"
              @drop="onEndDrop('expense')"
            >
              Gider tarafının sonuna bırak
            </div>
          </div>
        </div>
      </div>

      <!-- NÖTR ALAN (gelir/gidere etki etmez) -->
      <div class="mt-5">
        <div
          class="px-1 mb-2 text-sm font-bold tracking-wide uppercase text-slate-500 dark:text-slate-400"
        >
          ◆ Nötr Alan
          <span class="font-normal normal-case text-slate-400"
            >— ayrıştırdığın kalemler; gelir ve gidere etki etmez</span
          >
        </div>
        <div class="flex flex-col gap-2">
          <BankGroupCard
            v-for="g in transferGroups"
            :key="g.key"
            :group="g"
            :txns="groupTxns[g.key] || []"
            :loading="groupLoading[g.key]"
            :expanded="expanded[g.key]"
            :drag-kind="dragKind"
            :highlight="dragOverKey === g.key"
            :accepts="accepts(g)"
            :insert-edge="insertEdgeFor(g)"
            :muted="g.count === 0"
            :is-first="isFirst(g)"
            :is-last="isLast(g)"
            @toggle="toggle(g.key)"
            @move-up="moveGroup(g, -1)"
            @move-down="moveGroup(g, 1)"
            @cycle-side="cycleSide(g)"
            @group-dragstart="onGroupDragStart(g)"
            @group-dragend="clearDrag"
            @group-dragover="onGroupDragover(g, $event)"
            @card-dragover="dragOverKey = g.key"
            @card-dragleave="dragOverKey = ''"
            @card-drop="onCardDrop(g)"
            @txn-dragstart="onTxnDragStart"
            @txn-dragend="clearDrag"
          />
          <div
            v-if="dragKind === 'group'"
            class="flex items-center justify-center h-12 text-xs border-2 border-dashed rounded-lg transition-colors"
            :class="
              endActive('transfer')
                ? 'border-woot-400 bg-woot-100/70 text-woot-700 dark:bg-woot-900/40'
                : 'border-slate-200 text-slate-400 dark:border-slate-600'
            "
            @dragover="onEndOver('transfer', $event)"
            @drop="onEndDrop('transfer')"
          >
            Nötr alanın sonuna bırak
          </div>
        </div>
      </div>

      <!-- KÂR-ZARARA ESAS GİDER (sürükle-bırak havuzu) -->
      <div
        class="mt-6 border-2 rounded-xl transition-colors"
        :class="
          pnlZoneActive
            ? 'border-indigo-500 border-dashed bg-indigo-100 dark:bg-indigo-900/40'
            : pnlArmed
              ? 'border-indigo-400 border-dashed bg-indigo-50 dark:bg-indigo-900/25'
              : 'border-indigo-200 bg-indigo-50/40 dark:border-indigo-800 dark:bg-indigo-900/10'
        "
        @dragenter.prevent
        @dragover.prevent="pnlZoneActive = true"
        @dragleave="pnlZoneActive = false"
        @drop.prevent="onPnlDrop"
      >
        <!-- Sürükleme sırasında net "buraya bırak" işareti -->
        <div
          v-if="pnlArmed"
          class="px-4 py-2 text-sm font-semibold text-center text-indigo-600 dark:text-indigo-300"
        >
          ↓ Buraya bırak — bu gideri Kâr-Zarara dahil et
        </div>
        <div class="flex flex-wrap items-center justify-between gap-3 p-4">
          <div class="min-w-0">
            <div
              class="text-sm font-bold tracking-wide uppercase text-indigo-700 dark:text-indigo-300"
            >
              🎯 Kâr-Zarara Esas Gider
            </div>
            <div class="max-w-xl text-xs text-slate-500 dark:text-slate-400">
              Satış net kârından düşülecek genel giderler. Komple bir gider
              grubunu eklemek için kartındaki <b>🎯</b> butonuna bas; tek bir
              satırı doğrudan buraya sürükleyebilirsin. Kargo / komisyon /
              reklam / ürün maliyetini EKLEME — onlar satış kârında zaten
              düşülü.
            </div>
          </div>
          <div class="text-right shrink-0">
            <div class="text-xs text-slate-500">İşaretli toplam gider</div>
            <div
              class="text-2xl font-bold tabular-nums text-indigo-700 dark:text-indigo-300"
            >
              {{ tl(pnlTotalAbs) }}
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-3 px-4 pb-4">
          <div
            v-if="pnlGroups.length === 0 && oneOffs.length === 0"
            class="py-3 text-sm text-center border border-dashed rounded-lg text-indigo-400 border-indigo-200 dark:border-indigo-800"
          >
            Henüz boş — bir gider grubunun (SGK, Maaş, Faturalar, Vergi…)
            kartındaki 🎯 butonuna bas ya da tek bir gider satırını buraya
            sürükle.
          </div>

          <!-- işaretli gruplar -->
          <div v-if="pnlGroups.length" class="flex flex-wrap gap-2">
            <span
              v-for="g in pnlGroups"
              :key="g.key"
              class="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-white border rounded-full shadow-sm border-indigo-200 dark:bg-slate-800 dark:border-indigo-800"
            >
              <span class="text-slate-700 dark:text-slate-200"
                >{{ g.icon }} {{ g.label }}</span
              >
              <span class="tabular-nums text-ruby-600 dark:text-ruby-400">{{
                tl(g.total)
              }}</span>
              <button
                class="text-slate-400 hover:text-ruby-500"
                title="Çıkar"
                @click="removePnlGroup(g.key)"
              >
                ✕
              </button>
            </span>
          </div>

          <!-- tek tek eklenen satırlar -->
          <div
            v-if="oneOffs.length"
            class="bg-white border rounded-lg border-indigo-100 dark:bg-slate-800 dark:border-indigo-900"
          >
            <div class="px-3 py-2 text-xs text-slate-400">
              Tek tek eklenen satırlar ({{ oneOffs.length }})
            </div>
            <div
              v-for="t in oneOffs"
              :key="t.id"
              class="flex items-center justify-between gap-2 px-3 py-2 text-sm border-t border-slate-50 dark:border-slate-700/50"
            >
              <span
                class="truncate text-slate-600 dark:text-slate-300"
                :title="(t.counterparty || '') + ' — ' + (t.description || '')"
              >
                {{ t.txn_on }} · {{ t.counterparty || t.label }}
              </span>
              <span class="flex items-center gap-3 shrink-0">
                <span class="tabular-nums text-ruby-600 dark:text-ruby-400">{{
                  tl(t.amount)
                }}</span>
                <button
                  class="text-slate-400 hover:text-ruby-500"
                  title="Çıkar"
                  @click="removePnlTxn(t.id)"
                >
                  ✕
                </button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
